// src/components/simulation/ai-sandbox-page.tsx
import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { ContentPreviewTray } from "../content-preview-tray";
import { useAISandboxData } from "../../hooks/simulation/useAISandboxData";
import {
  runSandboxSimulation,
  fetchSandboxHistory,
  fetchSandboxRun,
  fetchSandboxSuggestions,
  extractMonitoringData,
  type SimulationQueueItem,
  type MonitoringBeforeData,
  type SandboxSuggestion,
} from "@/app/services/simulation/ai-sandbox-service";

// Single source of truth, sandbox picker contents come from MODEL_CATALOG.
// Which models appear is controlled by `inPicker`. Which are pre-selected
// is controlled by `recommended`. Names/providers/descriptions come from
// the matching catalog entries.
import { AVAILABLE_AI_MODELS } from "@/app/constants/models";

import { TestRefineShell } from "./redesign/test-refine-shell";

interface AISandboxPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  /** Passed directly from App.tsx, the live BrandMonitoringResponse object */
  antennaData?: any | null;
}

export function AISandboxPage({
  isSidebarCollapsed = false,
  onSidebarToggle,
  antennaData,
}: AISandboxPageProps) {
  const brand = localStorage.getItem("bm_brand") || "";
  const { data: sandboxData, setData: setSandboxData, loading, error } = useAISandboxData(brand);

  const monitoringData = useMemo((): MonitoringBeforeData | null => {
    if (antennaData && typeof antennaData === "object") {
      const extracted = extractMonitoringData(antennaData);
      if (extracted) return extracted;
    }
    try {
      const stored = localStorage.getItem("bm_last_result");
      if (stored) {
        const parsed = JSON.parse(stored);
        const extracted = extractMonitoringData(parsed);
        if (extracted) return extracted;
      }
    } catch {
      // ignore
    }
    return null;
  }, [antennaData]);

  // Picker list comes straight from the catalog. There is no hardcoded
  // model list in this file, to change what appears here, edit
  // MODEL_CATALOG in src/app/constants/models.ts (`inPicker` flag).
  const availableModels = AVAILABLE_AI_MODELS;

  // Initial selection = whatever the catalog says is recommended. If you
  // flip `recommended` on or off in models.ts, the default selection here
  // updates on the next page load.
  const initialSelectedModelIds = useMemo(
    () => AVAILABLE_AI_MODELS.filter((m) => m.recommended).map((m) => m.id),
    []
  );

  const [selectedModels, setSelectedModels] = useState<string[]>(initialSelectedModelIds);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("configure");
  const [isSimulating, setIsSimulating] = useState(false);
  const [hasRunSimulation, setHasRunSimulation] = useState(false);
  const [, setLoadedRunId] = useState<number | null>(null);
  const [appliedQueueItemsForResult, setAppliedQueueItemsForResult] = useState<SimulationQueueItem[]>([]);

  const [competitorInput, setCompetitorInput] = useState("");
  const [competitors, setCompetitors] = useState<string[]>([]);

  const [contextualUpdate, setContextualUpdate] = useState<{
    title: string; description: string; timestamp: number;
  } | undefined>(undefined);

  const [selectedQueueItems, setSelectedQueueItems] = useState<number[]>([]);
  const [contentPreviewItem, setContentPreviewItem] = useState<SimulationQueueItem | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // AI-suggested buyer questions to test. Claude reads the brand's monitoring
  // visibility + queued actions and proposes what is worth testing. Real
  // engine (POST /sandbox/suggest-queries); empty on failure so tiles hide.
  const [suggestions, setSuggestions] = useState<SandboxSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Opening the AI assistant (same mechanism as the rest of the app: set a
  // topic and un-collapse the sidebar, which prefills it).
  const [aiTopic, setAiTopic] = useState<string | undefined>(undefined);
  const handleOpenAIChat = (topic?: string) => {
    setAiTopic(topic);
    if (isSidebarCollapsed) onSidebarToggle?.(false);
  };
  useEffect(() => {
    if (!aiTopic) return;
    const t = setTimeout(() => setAiTopic(undefined), 500);
    return () => clearTimeout(t);
  }, [aiTopic]);

  const simulationQueueSafe = sandboxData?.simulationQueue || [];

  useEffect(() => {
    const q = sandboxData?.simulationQueue || [];
    if (!brand.trim() || q.length === 0) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setSuggestionsLoading(true);
    fetchSandboxSuggestions({ brand, monitoringData, queueItems: q })
      .then((res) => {
        if (cancelled) return;
        setSuggestions(Array.isArray(res?.suggestions) ? res.suggestions : []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, sandboxData?.simulationQueue, monitoringData]);

  const selectedQueueItemsPayload: SimulationQueueItem[] = useMemo(() => {
    const byId = new Map<number, SimulationQueueItem>();
    for (const q of simulationQueueSafe) byId.set(Number(q.id), q);
    return selectedQueueItems
      .map((id) => byId.get(Number(id)))
      .filter((item): item is SimulationQueueItem => Boolean(item));
  }, [selectedQueueItems, simulationQueueSafe]);

  const addCompetitor = () => {
    const trimmed = competitorInput.trim();
    if (trimmed && !competitors.includes(trimmed) && competitors.length < 5) {
      setCompetitors([...competitors, trimmed]);
      setCompetitorInput("");
    }
  };
  const removeCompetitor = (name: string) => setCompetitors(competitors.filter((c) => c !== name));

  const contentMarginClass = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-[380px]";

  // ── Loading / error ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div className={`flex-1 flex items-center justify-center transition-all duration-300 ${contentMarginClass}`}>
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--signal-blue)" }} />
            <p style={{ fontSize: "var(--text-base)", color: "var(--muted-foreground)" }}>Loading sandbox…</p>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="sandbox" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} />
        </div>
      </div>
    );
  }

  if (error || !sandboxData) {
    return (
      <div className="flex min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div className={`flex-1 flex items-center justify-center transition-all duration-300 ${contentMarginClass}`}>
          <div className="flex flex-col items-center gap-4 text-center max-w-md px-4">
            <AlertTriangle className="w-12 h-12" style={{ color: "var(--infrared-coral)" }} />
            <h3>Failed to load data</h3>
            <p style={{ color: "var(--muted-foreground)" }}>{error?.message || "Unable to load sandbox data."}</p>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="sandbox" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} />
        </div>
      </div>
    );
  }

  const mapAppliedActions = (actions: any[], createdAtIso?: string): SimulationQueueItem[] => {
    const ts = createdAtIso || new Date().toISOString();
    return (Array.isArray(actions) ? actions : [])
      .filter((a) => a && a.id !== undefined && a.id !== null)
      .map((a) => ({
        id: Number(a.id),
        title: String(a.title || "Untitled action"),
        type: "Recommended Action",
        source: "Antenna",
        confidence: 80,
        expectedImpact: typeof a.impact === "number" ? `${a.impact}` : String(a.impact || ""),
        summary: String(a.description || ""),
        lastModified: ts,
        priority: String(a.priority ?? "MEDIUM"),
        description: String(a.description ?? ""),
        impact: a.impact ?? "",
        effort: String(a.effort ?? "Medium"),
        strategicRationale: { issues: [], gaps: [], opportunities: [] },
      }));
  };

  // ── Run simulation ────────────────────────────────────────────────────────
  const handleRunSimulation = async () => {
    if (!brand?.trim()) {
      setContextualUpdate({ title: "Missing brand", description: "Brand not set.", timestamp: Date.now() });
      return;
    }
    if (!query.trim()) {
      setContextualUpdate({ title: "Missing query", description: "Enter a query to simulate.", timestamp: Date.now() });
      setActiveTab("configure");
      return;
    }
    if (!selectedModels.length) {
      setContextualUpdate({ title: "No models selected", description: "Select at least one model.", timestamp: Date.now() });
      setActiveTab("configure");
      return;
    }
    if (!selectedQueueItemsPayload.length) {
      setContextualUpdate({ title: "No queue items", description: "Select at least one queued action to simulate.", timestamp: Date.now() });
      setActiveTab("configure");
      return;
    }

    setIsSimulating(true);
    setHasRunSimulation(false);
    setActiveTab("results");

    const sourceLabel = monitoringData
      ? `monitoring baseline: ${monitoringData.overall.probability}% / rank #${monitoringData.overall.rank}`
      : "no monitoring baseline, LLM will estimate before scores";

    setContextualUpdate({
      title: "Simulation started",
      description: `Running ${selectedModels.length} model(s) with ${selectedQueueItemsPayload.length} action(s). Using ${sourceLabel}.`,
      timestamp: Date.now(),
    });

    try {
      const res = await runSandboxSimulation({
        brand,
        query: query.trim(),
        models: selectedModels,
        selectedQueueItems: selectedQueueItemsPayload,
        file: selectedFile || undefined,
        competitors,
        monitoringData,
      });

      setAppliedQueueItemsForResult(mapAppliedActions(res.selectedQueueItems || [], res.createdAt));
      setLoadedRunId(res.runId);

      const historyRes = await fetchSandboxHistory({ brand });

      setSandboxData((prev) => {
        if (!prev) return prev as any;
        return {
          ...prev,
          beforeAfterData: res.beforeAfterData,
          modelResults: res.modelResults,
          discoveries: res.discoveries || [],
          rationale: res.rationale || { issues: [], gaps: [], opportunities: [] },
          competitors: res.competitors || [],
          timeToImpact: res.timeToImpact || [],
          monitoringBeforeUsed: res.monitoringBeforeUsed,
          simulationHistory: historyRes.items || [],
        };
      });

      setIsSimulating(false);
      setHasRunSimulation(true);
      setContextualUpdate({
        title: "Simulation complete",
        description: res.monitoringBeforeUsed
          ? "Before scores from real monitoring data. After scores estimated by AI."
          : "Complete. Run Antenna first for real before scores.",
        timestamp: Date.now(),
      });
    } catch (e: any) {
      setIsSimulating(false);
      setContextualUpdate({
        title: "Simulation failed",
        description: e?.message || "Please try again.",
        timestamp: Date.now(),
      });
    }
  };

  // ── History click ─────────────────────────────────────────────────────────
  const handleHistoryClick = async (runId: number) => {
    try {
      setContextualUpdate({ title: "Loading run…", description: `Fetching run #${runId}`, timestamp: Date.now() });
      const run = await fetchSandboxRun(runId);
      setQuery(run.query || "");
      setLoadedRunId(run.runId);
      const applied = mapAppliedActions(run.selectedQueueItems || [], run.createdAt);
      setAppliedQueueItemsForResult(applied);
      setSelectedQueueItems(
        (run.selectedQueueItems || []).map((x: any) => Number(x.id)).filter((n: number) => Number.isFinite(n))
      );

      setSandboxData((prev) => {
        if (!prev) return prev as any;
        return {
          ...prev,
          beforeAfterData: run.beforeAfterData,
          modelResults: run.modelResults,
          discoveries: run.discoveries || [],
          rationale: run.rationale || { issues: [], gaps: [], opportunities: [] },
          competitors: (run as any).competitors || [],
          timeToImpact: (run as any).timeToImpact || [],
          monitoringBeforeUsed: (run as any).monitoringBeforeUsed,
        };
      });

      setHasRunSimulation(true);
      setIsSimulating(false);
      setActiveTab("results");
      setContextualUpdate({ title: "Run loaded", description: `Loaded run #${run.runId}.`, timestamp: Date.now() });
    } catch (e: any) {
      setContextualUpdate({ title: "Failed to load run", description: e?.message || "Unable to load.", timestamp: Date.now() });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const monitoringSource: "live" | "cached" | "none" = (() => {
    if (!monitoringData) return "none";
    if (antennaData && extractMonitoringData(antennaData)) return "live";
    return "cached";
  })();

  return (
    <>
      <div className="min-h-screen" data-kit style={{ backgroundColor: "var(--background)" }}>
        <div className={`transition-all duration-300 ${contentMarginClass}`}>
          <TestRefineShell
            brand={brand}
            sandboxData={sandboxData}
            monitoringData={monitoringData}
            monitoringSource={monitoringSource}
            availableModels={availableModels}
            selectedModels={selectedModels}
            setSelectedModels={setSelectedModels}
            onModelChangeContext={(id, added, count) =>
              setContextualUpdate({
                title: added ? `${id} added` : `${id} removed`,
                description: `Simulation runs across ${count} model(s).`,
                timestamp: Date.now(),
              })
            }
            query={query}
            setQuery={setQuery}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isSimulating={isSimulating}
            hasRunSimulation={hasRunSimulation}
            competitors={competitors}
            competitorInput={competitorInput}
            setCompetitorInput={setCompetitorInput}
            addCompetitor={addCompetitor}
            removeCompetitor={removeCompetitor}
            selectedQueueItems={selectedQueueItems}
            setSelectedQueueItems={setSelectedQueueItems}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            onViewContent={setContentPreviewItem}
            appliedQueueItems={appliedQueueItemsForResult}
            onRunSimulation={handleRunSimulation}
            onHistoryClick={handleHistoryClick}
            suggestions={suggestions}
            suggestionsLoading={suggestionsLoading}
            onOpenAIChat={handleOpenAIChat}
          />
        </div>
      </div>

      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="sandbox"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
          contextualUpdate={contextualUpdate}
          initialTopic={aiTopic}
        />
      </div>
      {contentPreviewItem && (
        <ContentPreviewTray item={contentPreviewItem} onClose={() => setContentPreviewItem(null)} />
      )}
    </>
  );
}
